import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  Icon,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdArrowForward } from "react-icons/md";
import { TransactionType } from "../../../../views/Orders/enums";
import { isInvalidDiscountAndCredit } from "../../../../utils/util";
import { usePatchEstimateSplit } from "../../../../views/Orders/hooks/usePatchEstimateSplit";
import { DeliveryOption } from "../../types";
import { extractApiErrorMessage } from "../../utils/apiErrorUtils";
import {
  buildEstimateSplitPatchPayload,
  canPersistEstimateSplitConfig,
} from "../../utils/estimateSplitPatchPayload";
import {
  resolveDefaultEstimateSplitTransactionType,
} from "../../utils/resolveDefaultEstimateSplitTransactionType";
import { isFacilitationTransactionType } from "../../utils/estimateSingleInvoiceValidation";
import { estimateSectionHeaderProps } from "../../utils/estimateSectionHeaderStyles";

const DEFAULT_GARAAZ_SUPPLIER_ID = "64ae87c051c0be36170dc718";

function sanitizeNonNegativePercentInput(raw: string): string {
  if (raw === "") {
    return "";
  }
  if (raw.includes("-")) {
    return "0";
  }
  const num = Number(raw);
  if (!Number.isNaN(num) && num < 0) {
    return "0";
  }
  return raw;
}

/** Matches part list row separators in PartsListColumn */
const dashedRowSeparator = {
  pb: 2,
  borderBottom: "1px dashed",
  borderColor: "gray.200",
};

interface SplitConfigurationSectionProps {
  estimateNo: string;
  splitId: string;
  supplierId?: string | null;
  orderData?: any;
  garaazSupplierIdFromConfig?: string;
  isSingleGaraazInvoiceRequired?: boolean;
  transactionType?: string;
  discountPercentage?: number;
  creditNotePercentage?: number;
  paymentRouteFromLabel: string;
  paymentRouteToLabel: string;
  hasSelectedDeliveryOption: boolean;
  isRemainingItems: boolean;
  onSplitConfigApplied: (update: {
    transactionType?: string;
    discountPercentage?: number;
    creditNotePercentage?: number;
    deliveryOptions: DeliveryOption[];
  }) => void;
}

function isGaraazSupplier(
  supplierId: string,
  garaazSupplierIdFromConfig?: string,
): boolean {
  return (
    !!supplierId &&
    supplierId === (garaazSupplierIdFromConfig || DEFAULT_GARAAZ_SUPPLIER_ID)
  );
}

export const SplitConfigurationSection: React.FC<
  SplitConfigurationSectionProps
> = ({
  estimateNo,
  splitId,
  supplierId,
  orderData,
  garaazSupplierIdFromConfig,
  isSingleGaraazInvoiceRequired = false,
  transactionType: transactionTypeProp,
  discountPercentage: discountProp,
  creditNotePercentage: creditProp,
  paymentRouteFromLabel,
  paymentRouteToLabel,
  hasSelectedDeliveryOption,
  isRemainingItems,
  onSplitConfigApplied,
}) => {
  const patchMutation = usePatchEstimateSplit();
  const [configError, setConfigError] = useState<string | null>(null);

  const canPersistSplitConfig = useMemo(
    () => canPersistEstimateSplitConfig(estimateNo, splitId),
    [estimateNo, splitId],
  );

  const defaultTransactionType = useMemo(
    () =>
      resolveDefaultEstimateSplitTransactionType({
        supplierId,
        orderData,
        isSingleGaraazInvoiceRequired,
        garaazSupplierIdFromConfig,
      }),
    [
      supplierId,
      orderData,
      isSingleGaraazInvoiceRequired,
      garaazSupplierIdFromConfig,
    ],
  );

  const [transactionType, setTransactionType] = useState(
    () => transactionTypeProp || defaultTransactionType || "",
  );
  const [discount, setDiscount] = useState(() =>
    discountProp != null ? String(discountProp) : "",
  );
  const [credit, setCredit] = useState(() =>
    creditProp != null ? String(creditProp) : "",
  );

  useEffect(() => {
    if (transactionTypeProp) {
      setTransactionType(transactionTypeProp);
      return;
    }

    const resolvedDefault = resolveDefaultEstimateSplitTransactionType({
      supplierId,
      orderData,
      isSingleGaraazInvoiceRequired,
      garaazSupplierIdFromConfig,
    });

    if (isSingleGaraazInvoiceRequired) {
      setTransactionType((prev) =>
        isFacilitationTransactionType(prev)
          ? TransactionType.TRADING
          : prev || TransactionType.TRADING,
      );
      return;
    }

    if (resolvedDefault) {
      setTransactionType(resolvedDefault);
    }
  }, [
    isSingleGaraazInvoiceRequired,
    transactionTypeProp,
    supplierId,
    orderData,
    garaazSupplierIdFromConfig,
  ]);

  useEffect(() => {
    setDiscount(discountProp != null ? String(discountProp) : "");
  }, [discountProp]);

  useEffect(() => {
    setCredit(creditProp != null ? String(creditProp) : "");
  }, [creditProp]);

  const isSaving =
    patchMutation.isLoading && patchMutation.variables?.splitId === splitId;

  const isFacilitationBlocked = useMemo(() => {
    if (!supplierId) return false;
    return (
      isGaraazSupplier(supplierId, garaazSupplierIdFromConfig) ||
      isSingleGaraazInvoiceRequired
    );
  }, [supplierId, garaazSupplierIdFromConfig, isSingleGaraazInvoiceRequired]);

  const facilitationBlockReason = useMemo(() => {
    if (!isFacilitationBlocked) return null;
    if (isSingleGaraazInvoiceRequired) {
      return "Facilitation not available when Single Invoice required";
    }
    return "Facilitation not available for Garaaz";
  }, [isFacilitationBlocked, isSingleGaraazInvoiceRequired]);

  const isTrading = transactionType === TransactionType.TRADING;

  const tradingFieldsValid = useMemo(() => {
    if (!isTrading) return true;
    return !isInvalidDiscountAndCredit(Number(discount), Number(credit));
  }, [isTrading, discount, credit]);

  const applySplitUpdateFromEstimate = useCallback(
    (estimate: any) => {
      const updatedSplit = (estimate?.splits ?? []).find(
        (s: any) => String(s._id) === String(splitId),
      );
      if (!updatedSplit) return;
      onSplitConfigApplied({
        transactionType: updatedSplit.transactionType,
        discountPercentage: updatedSplit.discountPercentage,
        creditNotePercentage: updatedSplit.creditNotePercentage,
        deliveryOptions: updatedSplit.deliveryOptions ?? [],
      });
      if (updatedSplit.transactionType) {
        setTransactionType(updatedSplit.transactionType);
      }
      if (updatedSplit.discountPercentage != null) {
        setDiscount(String(updatedSplit.discountPercentage));
      } else if (updatedSplit.transactionType !== TransactionType.TRADING) {
        setDiscount("");
      }
      if (updatedSplit.creditNotePercentage != null) {
        setCredit(String(updatedSplit.creditNotePercentage));
      } else if (updatedSplit.transactionType !== TransactionType.TRADING) {
        setCredit("");
      }
    },
    [splitId, onSplitConfigApplied],
  );

  const persistConfig = useCallback(
    (input: {
      transactionType?: string;
      discountPercentage?: number;
      creditNotePercentage?: number;
    }) => {
      if (!canPersistSplitConfig || isRemainingItems) return;

      const payload = buildEstimateSplitPatchPayload(input);
      if (
        !payload.transactionType &&
        payload.discountPercentage === undefined &&
        payload.creditNotePercentage === undefined
      ) {
        return;
      }

      setConfigError(null);
      patchMutation.mutate(
        { estimateNo, splitId, payload },
        {
          onSuccess: applySplitUpdateFromEstimate,
          onError: (error: unknown) => {
            setConfigError(
              extractApiErrorMessage(error, "Failed to update configuration."),
            );
          },
        },
      );
    },
    [
      canPersistSplitConfig,
      isRemainingItems,
      patchMutation,
      estimateNo,
      splitId,
      applySplitUpdateFromEstimate,
    ],
  );

  const handleTransactionTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      if (!value) {
        setTransactionType("");
        return;
      }
      setTransactionType(value);
      if (value === TransactionType.FACILITATION) {
        setDiscount("");
        setCredit("");
        persistConfig({ transactionType: value });
        return;
      }
      if (
        value === TransactionType.TRADING &&
        !isInvalidDiscountAndCredit(Number(discount), Number(credit))
      ) {
        persistConfig({
          transactionType: value,
          discountPercentage: Number(discount) || 0,
          creditNotePercentage: Number(credit) || 0,
        });
      }
    },
    [persistConfig, discount, credit],
  );

  const handleTradingBlur = useCallback(() => {
    if (!isTrading || !tradingFieldsValid || !transactionType) return;

    const discountTrim = discount.trim();
    const creditTrim = credit.trim();
    if (!discountTrim && !creditTrim) {
      return;
    }

    const discountNum = Math.max(0, Number(discountTrim) || 0);
    const creditNum = Number(creditTrim) || 0;
    const savedDiscount = discountProp ?? 0;
    const savedCredit = creditProp ?? 0;
    if (discountNum === savedDiscount && creditNum === savedCredit) {
      return;
    }

    persistConfig({
      transactionType,
      discountPercentage: discountNum,
      creditNotePercentage: creditNum,
    });
  }, [
    isTrading,
    tradingFieldsValid,
    transactionType,
    persistConfig,
    discount,
    credit,
    discountProp,
    creditProp,
  ]);

  const handleDiscountChange = useCallback((valueAsString: string) => {
    setDiscount(sanitizeNonNegativePercentInput(valueAsString));
  }, []);

  const handleDiscountBlur = useCallback(() => {
    if (discount.trim() && Number(discount) < 0) {
      setDiscount("0");
    }
    handleTradingBlur();
  }, [discount, handleTradingBlur]);

  const hasSelectedPaymentRoute =
    paymentRouteFromLabel !== "-" && paymentRouteToLabel !== "-";

  const paymentRouteValue = useMemo(() => {
    if (!hasSelectedDeliveryOption) {
      return {
        kind: "hint" as const,
        text: "Select a delivery option first",
      };
    }
    if (hasSelectedPaymentRoute) {
      return { kind: "route" as const };
    }
    return { kind: "empty" as const, text: "—" };
  }, [hasSelectedDeliveryOption, hasSelectedPaymentRoute]);

  if (isRemainingItems) {
    return null;
  }

  return (
    <Box pt={3} borderTop="1px solid" borderColor="gray.200">
      <Text {...estimateSectionHeaderProps} mb={2}>
        Configuration
      </Text>

      <VStack align="stretch" spacing={2}>
        <Box {...dashedRowSeparator}>
          <Flex align="flex-end" gap={3} w="100%" flexWrap="nowrap">
            <Box flex="1.4" minW={0} flexShrink={1}>
              <Text
                fontSize="0.75rem"
                color="gray.600"
                fontWeight="600"
                mb={1}
                fontFamily="'Nunito','Inter', sans-serif"
                whiteSpace="nowrap"
              >
                Transaction Type
              </Text>
              <Select
                value={transactionType}
                onChange={handleTransactionTypeChange}
                isDisabled={isSaving}
                size="sm"
                w="100%"
                borderRadius="md"
                fontFamily="'Nunito','Inter', sans-serif"
                fontSize="0.8rem"
                bg="white"
                focusBorderColor="blue.400"
              >
                <option value="">Select</option>
                {!isFacilitationBlocked && (
                  <option value={TransactionType.FACILITATION}>Facilitation</option>
                )}
                <option value={TransactionType.TRADING}>Trading</option>
              </Select>
            </Box>
            <Box flex="1" minW="64px">
              <Text
                fontSize="0.75rem"
                color={isTrading ? "gray.600" : "gray.400"}
                fontWeight="600"
                mb={1}
                fontFamily="'Nunito','Inter', sans-serif"
                whiteSpace="nowrap"
              >
                Discount (%)
              </Text>
              <NumberInput
                min={0}
                max={100}
                w="100%"
                value={discount}
                onChange={handleDiscountChange}
                onBlur={handleDiscountBlur}
                isDisabled={!isTrading || isSaving}
                size="sm"
                clampValueOnBlur
              >
                <NumberInputField borderRadius="md" placeholder="0" />
              </NumberInput>
            </Box>
            <Box flex="1" minW="64px">
              <Text
                fontSize="0.75rem"
                color={isTrading ? "gray.600" : "gray.400"}
                fontWeight="600"
                mb={1}
                fontFamily="'Nunito','Inter', sans-serif"
                whiteSpace="nowrap"
              >
                Credit (%)
              </Text>
              <NumberInput
                min={0}
                w="100%"
                value={credit}
                onChange={(valueAsString) => setCredit(valueAsString)}
                onBlur={handleTradingBlur}
                isDisabled={!isTrading || isSaving}
                size="sm"
              >
                <NumberInputField borderRadius="md" placeholder="0" />
              </NumberInput>
            </Box>
          </Flex>
          {!transactionType && (
            <Text
              fontSize="0.7rem"
              color="gray.500"
              mt={1.5}
              fontFamily="'Nunito','Inter', sans-serif"
            >
              Select a transaction type.
            </Text>
          )}
          {facilitationBlockReason && (
            <Text
              w="100%"
              fontSize="0.7rem"
              color="orange.500"
              mt={1.5}
              textAlign="right"
              fontFamily="'Nunito','Inter', sans-serif"
            >
              {facilitationBlockReason}
            </Text>
          )}
          {isTrading && !tradingFieldsValid && (
            <Text fontSize="0.7rem" color="orange.500" mt={1.5}>
              Enter valid Discount % (0–100) and Credit Note % (≥0).
            </Text>
          )}
        </Box>

        <Box {...dashedRowSeparator} borderBottom="none" pb={0}>
          <Flex align="center" justify="space-between" gap={3}>
            <Text
              fontSize="0.8rem"
              color="gray.600"
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight="600"
            >
              Payment Route
            </Text>
            {paymentRouteValue.kind === "route" ? (
              <Flex
                align="center"
                justify="flex-end"
                gap={1}
                fontSize="0.8rem"
                color="#6366F1"
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="900"
                lineHeight="1.2"
                textAlign="right"
              >
                <Text as="span" fontWeight="800">
                  {paymentRouteFromLabel}
                </Text>
                <Icon as={MdArrowForward} boxSize={3.5} />
                <Text as="span" fontWeight="800">
                  {paymentRouteToLabel}
                </Text>
              </Flex>
            ) : (
              <Text
                fontSize="0.75rem"
                color={
                  paymentRouteValue.kind === "hint" ? "orange.500" : "gray.400"
                }
                fontFamily="'Nunito','Inter', sans-serif"
                fontStyle="italic"
                fontWeight="600"
                textAlign="right"
              >
                {paymentRouteValue.text}
              </Text>
            )}
          </Flex>
          {hasSelectedDeliveryOption && (
            <Text
              fontSize="0.7rem"
              color="gray.500"
              fontStyle="italic"
              mt={1.5}
              fontFamily="'Nunito','Inter', sans-serif"
            >
              * Updates based on selected delivery option
            </Text>
          )}
        </Box>
      </VStack>

      {configError && (
        <Text fontSize="0.7rem" color="red.500" mt={2}>
          {configError}
        </Text>
      )}
    </Box>
  );
};
